import { type Role, RoleAssignment } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import type { RoleManagementProperties } from '@adornis/entity-relationship-management/client/admin-pages/role-management.js';
import type { SectionRendererSection } from '@adornis/entity-relationship-management/client/details/SectionRenderer.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import type { AdornisUser } from '@adornis/users/db/a-user.js';
import { html, nothing } from 'lit';
import { subscribePlatformRoleAssignmentFor } from '../nexus-api/platform-gated-reads.js';
import { getNexusRoleManagementPermissions } from '../platform-rbac/platform-admin-permissions.js';
import { subscribeAllEntitiesRoles } from '../subscribe-all-roles.js';
import type { NscRoleManagementProps } from './nsc-role-management-router.js';

/** ERM `roleManagement` block shared by SmartTeams and Nexus admin shells. */
export function platformRoleManagementErmPage(
  m: Record<string, () => string>,
  extras?: Partial<Pick<NscRoleManagementProps, 'roleNameFilter' | 'permissions'>>,
): RoleManagementProperties {
  const { permissions: permissionsOverride } = extras ?? {};
  return {
    permissions: permissionsOverride ?? getNexusRoleManagementPermissions(),
    m,
    access: {
      create: () => true,
      edit: () => true,
      delete: () => true,
    },
  };
}

export type UserRoleAssignmentSectionOptions = {
  title: string;
  /** When omitted, all roles from the Role collection are listed. */
  roleNameFilter?: (roleName: string | undefined) => boolean;
  placeholder: string;
  emptyState: string;
  hint?: string;
  dialogLoading?: string;
  dialogSuccess?: string;
  dialogErrorPrefix?: string;
};

type RolePickerProps = {
  user: AdornisUser<any>;
  placeholder: string;
  emptyState: string;
  roleNameFilter?: (roleName: string | undefined) => boolean;
  hint?: string;
  dialogLoading?: string;
  dialogSuccess?: string;
  dialogErrorPrefix?: string;
};

const RolePicker = stateful((props: RolePickerProps) => {
  const { user, roleNameFilter, placeholder, emptyState, hint, dialogLoading, dialogSuccess, dialogErrorPrefix } =
    props;
  const [refreshKey, setRefreshKey] = useState(0);
  const rolesQuery = useQuery(() => subscribeAllEntitiesRoles()({ _id: 1, roleName: 1, permissions: 1 }), []);
  const assignmentQuery = useQuery<RoleAssignment | null>(
    () => subscribePlatformRoleAssignmentFor(user._id)({ _id: 1, roleIds: 1, scope: { contexts: 1, permission: 1 } }),
    [refreshKey, user._id],
  );

  const allRoles = (rolesQuery.value ?? []) as Role[];
  const roles = roleNameFilter ? allRoles.filter(r => roleNameFilter(r.roleName ?? undefined)) : allRoles;
  const assignment = assignmentQuery.value;
  const selectedRoleIds = new Set(assignment?.roleIds ?? []);
  const selectedRoles = roles.filter(role => selectedRoleIds.has(role._id));
  const refresh = () => setRefreshKey(refreshKey + 1);

  const handleRoleSelectionChange = async (nextSelection: Role[]) => {
    const visibleRoleIds = new Set(roles.map(role => String(role._id)));
    const preservedRoleIds = (assignment?.roleIds ?? []).filter(id => !visibleRoleIds.has(String(id)));
    const nextRoleIds = [...new Set([...preservedRoleIds, ...nextSelection.map(role => String(role._id))])];
    const previousRoleIds = assignment?.roleIds ?? [];
    if (JSON.stringify([...previousRoleIds].sort()) === JSON.stringify([...nextRoleIds].sort())) return;

    await guardDialog(
      async () => {
        if (assignment) {
          assignment.roleIds = nextRoleIds;
          await assignment.save?.();
        } else if (nextRoleIds.length > 0) {
          await new RoleAssignment({ userId: user._id, roleIds: nextRoleIds }).create?.();
        }
      },
      {
        loadingText: dialogLoading ?? 'Roles are being updated…',
        successText: dialogSuccess ?? 'Roles updated.',
        errorMessagePrefix: dialogErrorPrefix ?? 'Roles could not be updated',
      },
    );
    refresh();
  };

  const isLoading = rolesQuery.loading || assignmentQuery.loading;

  if (rolesQuery.error) {
    return html`<div class="alert alert-error">Roles could not be loaded: ${rolesQuery.error.message}</div>`;
  }

  return html`
    <div class="flex flex-col gap-2">
      <div class="flex items-center justify-between">
        ${hint ? html`<p class="text-xs text-base-content/70">${hint}</p>` : nothing}
        ${isLoading ? html`<span class="loading loading-spinner loading-sm text-primary"></span>` : nothing}
      </div>
      <div class="rounded-xl border border-base-200 bg-base-100">
        ${DropdownSelectionInput<Role, true>({
          multi: true,
          placeholder,
          selectables: roles as Role[],
          value: selectedRoles as Role[],
          renderString: role => role.roleName ?? role._id,
          resolveID: role => role._id,
          emptyState,
          onPicked: value => handleRoleSelectionChange((value as Role[]) ?? []),
        })}
      </div>
    </div>
  `;
});

/** ERM user detail section: assign ABAC roles (shared SmartTeams / Nexus pattern). */
export function userRoleAssignmentSection(opts: UserRoleAssignmentSectionOptions): SectionRendererSection<AdornisUser> {
  const { title, roleNameFilter, placeholder, emptyState, hint, dialogLoading, dialogSuccess, dialogErrorPrefix } =
    opts;
  return {
    title,
    fields: [
      {
        render({ entity: user }) {
          if (!user) return nothing;
          return RolePicker({
            user,
            roleNameFilter,
            placeholder,
            emptyState,
            hint,
            dialogLoading,
            dialogSuccess,
            dialogErrorPrefix,
          });
        },
      },
    ],
  };
}
